import * as React from 'react';
import { Alert } from '~/components/Alert';
import { Breadcrumb } from '~/components/Breadcrumps';
import { Button } from '~/components/Button';
import { CollapsibleSection } from '~/components/CollapsibleSection';
import { DetailsView } from '~/components/DetailsView';
import detailsStyle from '~/components/DetailsView/index.module.scss';
import { JsonViewerModal } from '~/components/JsonViewerModal';
import { useModals } from '~/components/Modal';
import { BreadcrumbsResourcePage, ResourceBlock } from '~/components/ResourcePage';
import { useAbility } from '~/lib/ability';
import { renderBoolean } from '~/lib/helpers';
import { formatJsonValue } from '~/lib/json';
import { ResourceLoader, useResource } from '~/lib/loader';
import { clientsLinks } from '~/modules/Clients';
import { ClientsAbility } from '~/modules/Clients/ability';
import { fetchSeller } from '~/modules/Clients/api';
import { SelfServeSellerSources } from '~/modules/Clients/components/SelfServeSources';
import { SellerBlockings } from '~/modules/Clients/components/SellerBlockings';
import { SellerSensitiveCategories } from '~/modules/Clients/components/SellerSensitiveCategories';
import { configurationToDetailsView } from '~/modules/Clients/mappers';
import { Configuration, configurationPresent } from '~/modules/Clients/models/Configuration';

const getConfigurationFields = (configuration: Configuration) => {
    if (!configurationPresent(configuration)) {
        return null;
    }
    if (typeof configuration !== 'string') {
        return null;
    }
    try {
        return configurationToDetailsView(configuration).sort((a, b) => a.label.localeCompare(b.label));
    } catch {
        return [];
    }
};

export const SelfServeSellerView = () => {
    const ability = useAbility(ClientsAbility);
    const sellerId = ability.sellerId();
    const modal = useModals();
    const jsonViewerModal = modal.create(JsonViewerModal);

    const sellerResult = useResource(() => fetchSeller(sellerId));

    return <ResourceLoader result={sellerResult} message='Loading seller'>
        {({ resource: seller }) => {
            const configurationFields = getConfigurationFields(seller.configuration);
            const configurationText = configurationFields ? formatJsonValue(seller.configuration) : '';
            return <>
            <Breadcrumb breadcrumb={{
                to: clientsLinks.selfServeSeller(),
                title: seller.name
            }}><BreadcrumbsResourcePage title='Seller'>
                    <DetailsView
                        fields={[
                            { label: 'Name', value: seller.name },
                            { label: 'Active', value: renderBoolean(seller.active) }
                        ]} />

                    {ability.isSelfServe() && (
                        <div className="layout-content-box">
                            <SellerBlockings sellerId={sellerId} />
                        </div>
                    )}
                    <div className="layout-content-box">
                        <SellerSensitiveCategories sellerId={sellerId} />
                    </div>
                    {ability.isSelfServe() && configurationFields && (
                                <ResourceBlock title="Configuration">
                                    <CollapsibleSection
                                        collapsedHeight={220}
                                        canCopy
                                        copyText={configurationText}
                                        footerActions={
                                            <Button
                                                small
                                                onClick={() =>
                                                    jsonViewerModal.show(
                                                        { title: 'Configuration', value: seller.configuration },
                                                        { size: 'wide' }
                                                    )
                                                }
                                            >
                                                View JSON
                                            </Button>
                                        }
                                    >
                                        {configurationFields.length ? (
                                            <div className={detailsStyle.detailsView}>
                                                {configurationFields.map((field) => (
                                                    <React.Fragment key={field.label}>
                                                        <div className={detailsStyle.detailsLabel}>{field.label}:</div>
                                                        <div className={detailsStyle.detailsValue}>{field.value}</div>
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        ) : (
                                            <div>Invalid configuration</div>
                                        )}
                                    </CollapsibleSection>
                                </ResourceBlock>
                    )}
                    <SelfServeSellerSources sellerId={sellerId} />
                </BreadcrumbsResourcePage>
            </Breadcrumb>
        </>;
        }}
    </ResourceLoader>;
};
